const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
    ? 'http://localhost:8000/api' 
    : '/api');

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    // Remove localStorage token usage - now using httpOnly cookies
    this.csrfToken = null;
    this.initializeCSRF();
  }

  async initializeCSRF() {
    try {
      const response = await fetch(`${this.baseURL}/csrf-token`, {
        credentials: 'include'
      });
      const data = await response.json();
      this.csrfToken = data.csrfToken;
    } catch (error) {
      console.warn('Failed to obtain CSRF token:', error.message);
    }
  }

  async refreshCSRFToken() {
    await this.initializeCSRF();
  }

  // Token methods removed - tokens now managed via httpOnly cookies
  async logout() {
    try {
      const result = await this.request('/auth/logout', {
        method: 'POST'
      });
      return result;
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }

  async verifyAuth() {
    try {
      const result = await this.request('/auth/verify');
      return result;
    } catch (error) {
      return null; // User not authenticated
    }
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Ensure CSRF token is available for state-changing operations
    const needsCSRF = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method);
    if (needsCSRF && !this.csrfToken) {
      await this.refreshCSRFToken();
    }
    
    const isFormData = options.body instanceof FormData;
    const config = {
      credentials: 'include',
      headers: {
        // Don't set Content-Type for FormData - browser sets it with boundary
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(this.csrfToken && needsCSRF && { 'X-CSRF-Token': this.csrfToken }),
        ...options.headers,
      },
      ...options,
    };

    console.log('Making API request to:', url);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    try {
      const response = await fetch(url, {
        ...config,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      // Handle CSRF token refresh on 403 errors
      if (error.message.includes('CSRF') && needsCSRF) {
        await this.refreshCSRFToken();
        // Retry the request once with new CSRF token
        config.headers['X-CSRF-Token'] = this.csrfToken;
        try {
          const retryResponse = await fetch(url, config);
          if (!retryResponse.ok) {
            const errorData = await retryResponse.json().catch(() => null);
            throw new Error(errorData?.message || `HTTP error! status: ${retryResponse.status}`);
          }
          return retryResponse.json();
        } catch (retryError) {
          throw retryError;
        }
      }
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please check if the backend server is running');
      }
      throw error;
    }
  }

  // Auth endpoints
  async login(credentials) {
    try {
      console.log('Attempting login with credentials:', { username: credentials.username });
      const result = await this.request('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });
      console.log('Login successful:', result);
      return result;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async changePassword(data) {
    return this.request('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getUsers() {
    return this.request('/auth/users');
  }

  async updateUser(id, data) {
    return this.request(`/auth/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async resetUserPassword(id, newPassword) {
    return this.request(`/auth/users/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ newPassword }),
    });
  }

  async registerUser(data) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Pages endpoints
  async getPages() {
    return this.request('/pages');
  }

  async getPage(pageId) {
    return this.request(`/pages/${pageId}`);
  }

  async updatePage(pageId, data) {
    return this.request(`/pages/${pageId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createPage(data) {
    return this.request('/pages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deletePage(pageId) {
    return this.request(`/pages/${pageId}`, {
      method: 'DELETE',
    });
  }

  async togglePagePublish(pageId) {
    return this.request(`/pages/${pageId}/publish`, {
      method: 'PATCH',
    });
  }

  // Announcements endpoints
  async getAnnouncements(params) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return this.request(`/announcements${queryString ? `?${queryString}` : ''}`);
  }

  async getAnnouncement(id) {
    return this.request(`/announcements/${id}`);
  }

  async createAnnouncement(data) {
    return this.request('/announcements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAnnouncement(id, data) {
    return this.request(`/announcements/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAnnouncement(id) {
    return this.request(`/announcements/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleAnnouncementStatus(id) {
    return this.request(`/announcements/${id}/toggle`, {
      method: 'PATCH',
    });
  }

  async toggleAnnouncementPin(id) {
    return this.request(`/announcements/${id}/pin`, {
      method: 'PATCH',
    });
  }

  // Events endpoints
  async getEvents(params) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return this.request(`/events${queryString ? `?${queryString}` : ''}`);
  }

  async getEvent(id) {
    return this.request(`/events/${id}`);
  }

  async createEvent(data) {
    return this.request('/events', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEvent(id, data) {
    return this.request(`/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEvent(id) {
    return this.request(`/events/${id}`, {
      method: 'DELETE',
    });
  }

  async updateEventStatus(id, status) {
    return this.request(`/events/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async toggleEventFeature(id) {
    return this.request(`/events/${id}/feature`, {
      method: 'PATCH',
    });
  }

  // Contacts endpoints
  async submitContact(data) {
    return this.request('/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getContacts(params) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return this.request(`/contact${queryString ? `?${queryString}` : ''}`);
  }

  async getContact(id) {
    return this.request(`/contact/${id}`);
  }

  async updateContactStatus(id, data) {
    return this.request(`/contact/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async respondToContact(id, response) {
    return this.request(`/contact/${id}/respond`, {
      method: 'PUT',
      body: JSON.stringify({ response }),
    });
  }

  async deleteContact(id) {
    return this.request(`/contact/${id}`, {
      method: 'DELETE',
    });
  }

  async getContactStats() {
    return this.request('/contact/stats/dashboard');
  }

  // Upload endpoints
  async uploadFile(file, type = 'general') {
    const formData = new FormData();
    formData.append('file', file);

    return this.request(`/uploads/single/${type}`, {
      method: 'POST',
      headers: {
        // Authorization header removed - using httpOnly cookies
      },
      body: formData,
    });
  }

  async uploadFiles(files, type = 'general') {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    return this.request(`/uploads/${type}`, {
      method: 'POST',
      headers: {
        // Authorization header removed - using httpOnly cookies
      },
      body: formData,
    });
  }

  async getUploadedFiles(type = 'general') {
    return this.request(`/uploads/${type}`);
  }

  async deleteFile(type, filename) {
    return this.request(`/uploads/${type}/${filename}`, {
      method: 'DELETE',
    });
  }

  async uploadImage(formData, config = {}) {
    const url = `${this.baseURL}/uploads/image`;
    
    // Ensure CSRF token is available for POST operations
    if (!this.csrfToken) {
      await this.refreshCSRFToken();
    }
    
    const headers = {};
    if (this.csrfToken) {
      headers['X-CSRF-Token'] = this.csrfToken;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
    }

    return { data: await response.json() };
  }

  // Homepage endpoints
  async getHomepage() {
    return this.request('/homepage');
  }

  async updateHomepage(id, data) {
    return this.request(`/homepage/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createHomepage(data) {
    return this.request('/homepage', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Vision Mission endpoints
  async getVisionMission() {
    return this.request('/vision-mission');
  }

  async getVisionMissionById(id) {
    return this.request(`/vision-mission/${id}`);
  }

  async updateVisionMission(id, data) {
    return this.request(`/vision-mission/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createVisionMission(data) {
    return this.request('/vision-mission', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteVisionMission(id) {
    return this.request(`/vision-mission/${id}`, {
      method: 'DELETE',
    });
  }

  // Chairman Message endpoints
  async getChairmanMessage() {
    return this.request('/chairman-message');
  }

  async getChairmanMessageById(id) {
    return this.request(`/chairman-message/${id}`);
  }

  async updateChairmanMessage(id, data) {
    return this.request(`/chairman-message/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createChairmanMessage(data) {
    return this.request('/chairman-message', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteChairmanMessage(id) {
    return this.request(`/chairman-message/${id}`, {
      method: 'DELETE',
    });
  }

  // Secretary Message endpoints
  async getSecretaryMessage() {
    return this.request('/secretary-message');
  }

  async getSecretaryMessageById(id) {
    return this.request(`/secretary-message/${id}`);
  }

  async updateSecretaryMessage(id, data) {
    return this.request(`/secretary-message/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createSecretaryMessage(data) {
    return this.request('/secretary-message', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteSecretaryMessage(id) {
    return this.request(`/secretary-message/${id}`, {
      method: 'DELETE',
    });
  }

  // Joint Secretary Message endpoints
  async getJointSecretaryMessage() {
    return this.request('/joint-secretary-message');
  }

  async getJointSecretaryMessageById(id) {
    return this.request(`/joint-secretary-message/${id}`);
  }

  async updateJointSecretaryMessage(id, data) {
    return this.request(`/joint-secretary-message/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createJointSecretaryMessage(data) {
    return this.request('/joint-secretary-message', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteJointSecretaryMessage(id) {
    return this.request(`/joint-secretary-message/${id}`, {
      method: 'DELETE',
    });
  }

  // Director Message endpoints
  async getDirectorMessage() {
    return this.request('/director-message');
  }

  async getDirectorMessageById(id) {
    return this.request(`/director-message/${id}`);
  }

  async updateDirectorMessage(id, data) {
    return this.request(`/director-message/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createDirectorMessage(data) {
    return this.request('/director-message', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteDirectorMessage(id) {
    return this.request(`/director-message/${id}`, {
      method: 'DELETE',
    });
  }

  // Approvals Affiliations endpoints
  async getApprovalsAffiliations() {
    return this.request('/approvals-affiliations');
  }

  async getApprovalsAffiliationsById(id) {
    return this.request(`/approvals-affiliations/${id}`);
  }

  async updateApprovalsAffiliations(id, data) {
    return this.request(`/approvals-affiliations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createApprovalsAffiliations(data) {
    return this.request('/approvals-affiliations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteApprovalsAffiliations(id) {
    return this.request(`/approvals-affiliations/${id}`, {
      method: 'DELETE',
    });
  }

  // Placements endpoints
  async getPlacements() {
    return this.request('/placements');
  }

  async getPlacementsById(id) {
    return this.request(`/placements/${id}`);
  }

  async updatePlacements(id, data) {
    return this.request(`/placements/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createPlacements(data) {
    return this.request('/placements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deletePlacements(id) {
    return this.request(`/placements/${id}`, {
      method: 'DELETE',
    });
  }

  // Research endpoints
  async getResearch() {
    return this.request('/research');
  }

  async getResearchById(id) {
    return this.request(`/research/${id}`);
  }

  async updateResearch(id, data) {
    return this.request(`/research/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createResearch(data) {
    return this.request('/research', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteResearch(id) {
    return this.request(`/research/${id}`, {
      method: 'DELETE',
    });
  }

  // Events Page Content endpoints
  async getEventsPageContent() {
    return this.request('/events-page-content');
  }

  async updateEventsPageContent(data) {
    return this.request('/events-page-content', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Infrastructure endpoints
  async getInfrastructure() {
    return this.request('/infrastructure');
  }

  async getInfrastructureById(id) {
    return this.request(`/infrastructure/${id}`);
  }

  async updateInfrastructure(id, data) {
    return this.request(`/infrastructure/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createInfrastructure(data) {
    return this.request('/infrastructure', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteInfrastructure(id) {
    return this.request(`/infrastructure/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleInfrastructurePublish(id) {
    return this.request(`/infrastructure/${id}/publish`, {
      method: 'PATCH',
    });
  }

  // Academics endpoints
  async getAcademics() {
    return this.request('/academics');
  }

  async getAcademicsAdmin() {
    return this.request('/academics/admin');
  }

  async updateAcademics(data) {
    return this.request('/academics', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Admissions endpoints
  async getAdmissions() {
    return this.request('/admissions');
  }

  async getAdmissionsAdmin() {
    return this.request('/admissions/admin');
  }

  async updateAdmissions(data) {
    return this.request('/admissions', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // SMTP Settings endpoints
  async getSMTPSettings() {
    return this.request('/smtp-settings');
  }

  async updateSMTPSettings(data) {
    return this.request('/smtp-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async testSMTPSettings(toEmail) {
    return this.request('/smtp-settings/test', {
      method: 'POST',
      body: JSON.stringify({ to: toEmail }),
    });
  }

  // Admission Enquiry endpoints
  async submitAdmissionEnquiry(data) {
    return this.request('/admission-enquiry/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAdmissionEnquiries(params) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return this.request(`/admission-enquiry${queryString ? `?${queryString}` : ''}`);
  }

  async updateAdmissionEnquiryStatus(id, data) {
    return this.request(`/admission-enquiry/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Grievance endpoints
  async submitGrievance(data) {
    return this.request('/grievances/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getGrievances(params) {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return this.request(`/grievances${queryString ? `?${queryString}` : ''}`);
  }

  async getGrievance(id) {
    return this.request(`/grievances/${id}`);
  }

  async updateGrievanceStatus(id, data) {
    return this.request(`/grievances/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteGrievance(id) {
    return this.request(`/grievances/${id}`, {
      method: 'DELETE',
    });
  }

  async getGrievanceStats() {
    return this.request('/grievances/stats/dashboard');
  }
}

export const apiService = new ApiService();